import type { ReactNode } from 'react';
import type { ApprovalRequest } from '@/lib/types';
import { formatPKR } from '../format';
import { BucketShell } from './_BucketShell';
import { readStructuredFields } from './_payload';

export interface AmountMismatchViewProps {
  req: ApprovalRequest;
}

function readNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function readText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

function readAmount(value: unknown): string {
  return formatPKR(readNumber(value));
}

function readMismatch(sf: Record<string, unknown>): Record<string, unknown> {
  const mismatch = sf['amount_mismatch'];
  return isRecord(mismatch) ? mismatch : {};
}

function readMismatchCandidates(
  sf: Record<string, unknown>,
  mismatch: Record<string, unknown>,
): Record<string, unknown>[] {
  const candidates = [
    mismatch['grn_candidates'],
    sf['postable_grn_candidates'],
    sf['recommended_grn_candidates'],
    sf['grn_candidates'],
  ];
  const seen = new Set<string>();
  return candidates
    .flatMap((value) =>
      Array.isArray(value)
        ? value.filter((item): item is Record<string, unknown> => isRecord(item))
        : [],
    )
    .filter((candidate) => {
      const key =
        readText(candidate['grn_det_id'] ?? candidate['grn_detail_id']) !== '—'
          ? `detail:${readText(candidate['grn_det_id'] ?? candidate['grn_detail_id'])}`
          : `grn:${readText(candidate['grn_no'])}:po:${readText(candidate['po_no'])}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export function AmountMismatchView({ req }: AmountMismatchViewProps) {
  const sf = readStructuredFields(req);
  const mismatch = readMismatch(sf);
  const grnTotal = readNumber(mismatch['grn_net_amount'] ?? mismatch['grn_total'] ?? sf['grn_total']);
  const subtotal = readNumber(mismatch['invoice_subtotal'] ?? sf['invoice_subtotal']);
  const delta = readNumber(mismatch['net_delta'] ?? mismatch['delta'] ?? sf['delta']);
  const amountBasisHint = readText(mismatch['amount_basis_hint']);
  const suggestedSubtotal = readNumber(mismatch['suggested_invoice_subtotal']);
  const suggestedGross = readNumber(mismatch['suggested_invoice_gross']);
  const candidates = readMismatchCandidates(sf, mismatch);

  // Picker lives in ReprocessForm to avoid duplicate UIs on the same row.
  return (
    <BucketShell req={req}>
      <section className="rounded-lg border border-orange-300 bg-orange-50 p-16">
        <p className="text-xs font-semibold uppercase tracking-wide text-orange-800">
          Amount mismatch
        </p>
        <div className="mt-12 grid gap-12 tablet:grid-cols-3">
          <Cell label="GRN total" value={grnTotal == null ? '—' : formatPKR(grnTotal)} />
          <Cell
            label="Invoice subtotal"
            value={subtotal == null ? '—' : formatPKR(subtotal)}
          />
          <Cell label="Delta" value={delta == null ? '—' : formatPKR(delta)} />
        </div>
        <p className="mt-12 text-xs text-neutral-600">
          Pick a GRN in the form below or override the subtotal manually, then save & validate.
        </p>
        {amountBasisHint === 'invoice_amount_appears_pre_tax' ? (
          <p className="mt-8 text-xs text-orange-900">
            The printed invoice amount appears to be before tax. Suggested subtotal:{' '}
            <span className="font-mono font-semibold">{readAmount(suggestedSubtotal)}</span>;
            suggested gross:{' '}
            <span className="font-mono font-semibold">{readAmount(suggestedGross)}</span>.
          </p>
        ) : null}
        {candidates.length > 0 ? (
          <div className="mt-12 overflow-x-auto rounded-md border border-orange-200 bg-neutral-0">
            <table className="min-w-full divide-y divide-orange-100 text-sm">
              <thead className="bg-orange-100/60 text-xs font-semibold uppercase tracking-wide text-orange-900">
                <tr>
                  <Th>GRN</Th>
                  <Th>Detail</Th>
                  <Th>PO</Th>
                  <Th>Item</Th>
                  <Th>Qty</Th>
                  <Th>Rate</Th>
                  <Th>Net delta</Th>
                  <Th>Tax delta</Th>
                  <Th>Gross delta</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-orange-100">
                {candidates.map((candidate, index) => (
                  <tr key={`${readText(candidate['grn_det_id'])}-${index}`}>
                    <Td mono>{readText(candidate['grn_no'])}</Td>
                    <Td mono>{readText(candidate['grn_det_id'] ?? candidate['grn_detail_id'])}</Td>
                    <Td mono>{readText(candidate['po_no'])}</Td>
                    <Td>
                      <div className="max-w-[260px]">
                        <p className="font-mono text-xs font-semibold text-neutral-900">
                          {readText(candidate['item_code'])}
                        </p>
                        <p className="mt-2 whitespace-normal text-xs text-neutral-600">
                          {readText(candidate['item_desc'] ?? candidate['detail_item_desc'])}
                        </p>
                      </div>
                    </Td>
                    <Td mono>{readText(candidate['quantity'])}</Td>
                    <Td mono>{readText(candidate['rate'] ?? candidate['po_rate'])}</Td>
                    <Td mono>{readAmount(candidate['net_delta'])}</Td>
                    <Td mono>{readAmount(candidate['tax_delta'])}</Td>
                    <Td mono>{readAmount(candidate['gross_delta'])}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </BucketShell>
  );
}

function Cell({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-orange-200 bg-neutral-0 p-12">
      <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">{label}</p>
      <p className="mt-4 font-mono text-sm font-semibold text-neutral-900">{value}</p>
    </div>
  );
}

function Th({ children }: { children: ReactNode }) {
  return <th className="px-12 py-8 text-left">{children}</th>;
}

function Td({ children, mono = false }: { children: ReactNode; mono?: boolean }) {
  return (
    <td
      className={`px-12 py-10 align-top text-neutral-800 ${
        mono ? 'font-mono text-xs font-semibold' : ''
      }`}
    >
      {children}
    </td>
  );
}
